import type { Address } from "viem"
import { useCallback, useMemo } from "react"
import { useTokenBalances } from "@/domains/api/hooks/use-token-balances"
import { useChainConfig } from "@/hooks/use-chain-config"
import { useLocalStorage } from "usehooks-ts"
import { isAddressEqual } from "viem"
import { useConfig } from "wagmi"

import type { Token } from "@/domains/core"
import { getTokenMetadata } from "@/domains/on-chain"

import useEventEmitter from "./useEventEmitter"

type UseLocalTokensProps = {
  account?: Address
  equalensAddress: Address
  onTokenAdded?: (token: Token) => void
  onTokenRemoved?: (token: Token) => void
}

export const useLocalTokens = ({ account, equalensAddress, onTokenAdded, onTokenRemoved }: UseLocalTokensProps) => {
  const chainConfig = useChainConfig()
  const config = useConfig()
  const emitter = useEventEmitter()

  // we manage the tokens separately
  const [internalLocalTokens, setLocalTokens] = useLocalStorage<Array<Token>>(`${chainConfig.slug}-local-tokens`, [])

  // now we need to pole and inject token balances
  const { balances } = useTokenBalances({
    account,
    tokens: internalLocalTokens,
    config,
    equalensAddress,
    isLocalTokenBalances: true,
  })

  // balances =  Record<Address, { value: number }>
  const localTokens = useMemo(() => {
    return internalLocalTokens.map((token) => {
      // balances MUST default to 0 if not returned, we MUST not filter out token addresses with 0 balances
      let balance = { value: "0" }
      if (balances?.[token.address]) {
        balance = balances[token.address] as { value: string }
      }
      return { ...token, balance: balance.value, balanceUsdValue: 0 }
    })
  }, [balances, internalLocalTokens])

  const handleAddToken = useCallback(
    async (tokenAddress: Address) => {
      const tokenMetadata = await getTokenMetadata({
        config,
        tokenAddress,
        chainId: chainConfig.chainId,
      })

      if (tokenMetadata === null) {
        // this breaks the app, we dont catch the error, investigate that
        // throw new Error(`Failed to fetch metadata for ${tokenAddress}`)
        return undefined
      }

      const tokenObject = {
        ...tokenMetadata,
        balance: "0",
        balanceUsdValue: 0,
      } satisfies Token

      setLocalTokens((prevTokens) => {
        // if the token is already in local storage, throw an error
        if (prevTokens.some((t) => isAddressEqual(t.address, tokenMetadata.address)))
          throw new Error("Token already in local storage")

        // if there is a callback for token addition, call it
        if (onTokenAdded) {
          onTokenAdded(tokenObject)
        }

        // copy the tokens array and add the token, we need to ensure a new array is returned to trigger a re-render
        const tokensCopy = [...prevTokens]

        tokensCopy.push(tokenObject)
        return tokensCopy
      })

      emitter.emit("local-token-added", {
        title: `Added local token`,
        description: `Added ${tokenMetadata.symbol} to your local tokens at ${tokenMetadata.address}`,
        variant: "success",
      })
    },
    [setLocalTokens],
  )

  const handleRemoveToken = useCallback(
    (address: Address) => {
      setLocalTokens((prevTokens) => {
        // if the token is not in local storage, throw an error
        const indexOfToken = prevTokens.findIndex((t) => isAddressEqual(t.address, address))
        if (indexOfToken === -1) throw new Error("Token not found in local storage")

        // if there is a callback for token removal, call it
        if (onTokenRemoved) {
          const tokenToRemove = prevTokens[indexOfToken]
          if (tokenToRemove) onTokenRemoved(tokenToRemove)
        }

        // copy the tokens array and remove the token, we need to ensure a new array is returned to trigger a re-render
        const tokensCopy = [...prevTokens]
        tokensCopy.splice(indexOfToken, 1)
        return tokensCopy
      })
    },
    [setLocalTokens],
  )

  return {
    localTokens,
    handleAddToken,
    handleRemoveToken,
  }
}
